import * as React from 'react';
import { useDispatch } from 'react-redux';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import { showAlert } from 'actions/app';
import clsx from 'clsx';
import { THEME } from 'GlobalStyles';
import isNil from 'lodash/isNil';
import strings from 'strings';
import FPAvatar from 'UI/components/atoms/FPAvatar';
import FPLink from 'UI/components/atoms/FPLink';
import { colors, CopyIcon } from 'UI/res';
import { copyToClipboard } from 'UI/utils/clipboard';

import { styles, useStyles } from './styles';

/** @typedef {import('types/app').LinkConfigProps} LinkConfigProps */
export const InfoLabelModes = Object.freeze({
  TEXT: 'text',
  LOCAL_LINK: 'localLink',
  EXTERNAL_LINK: 'externalLink',
  RING_CENTRAL: 'ringCentral',
  CHIP: 'chip'
});

const InfoLabelChips = ({ values, labelKey, baseClass }) => {
  const classes = useStyles();

  return values.map((item, index) => {
    const label = item?.[labelKey] ?? item.label ?? item;
    const key = !isNil(item?.id) ? `${label}-${item.id}` : `${label}-${index}`;

    const className = baseClass && !isNil(item?.id) ? `${baseClass} ${baseClass}-${item.id}` : '';

    return (
      <Chip
        key={key}
        label={label}
        className={className}
        classes={{ root: classes.chip }}
        size="small"
      />
    );
  });
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {(string|Array<Object>)} [props.description='']
 * @param {boolean} [props.cropped=true]
 * @param {*} [props.colorIndicator]
 * @param {(boolean|null)} [props.isLoading=false]
 * @param {LinkConfigProps} [props.linkProps]
 * @param {('text'|'localLink'|'externalLink'|'ringCentral'|'chip')} [props.mode='text']
 * @param {*} [props.customValueRender]
 * @param {boolean} [props.canCopy=false]
 * @param {string} [props.className]
 * @param {Object} [props.avatarProps]
 * @param {string} [props.itemId='']
 * @param {string} [props.variant='subtitle1']
 * @param {Object} [props.textProps]
 * @param {string} [props.secondaryText]
 * @param {React.ReactNode} [props.Icon]
 */
const InfoLabel = ({
  title,
  description,
  canCopy,
  cropped,
  isLoading,
  linkProps,
  customValueRender,
  mode,
  avatarProps,
  className,
  itemId,
  variant,
  textProps,
  secondaryText,
  Icon,
  labelKey,
  baseClass
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const elementToCopy = mode === InfoLabelModes.EXTERNAL_LINK ? linkProps?.url : description;

  const handleCopyClick = async e => {
    e.preventDefault();
    e.stopPropagation();
    await copyToClipboard(elementToCopy);
    dispatch(
      showAlert({
        severity: 'success',
        title: strings.shared.ui.awesome,
        body: strings.shared.ui.copiedToClipboard,
        autoHideDuration: 1000
      })
    );
  };

  const renderCopyButton = ({ color }) =>
    canCopy &&
    elementToCopy && (
      <CopyIcon
        fill={color}
        size={16}
        className={clsx(classes.copyButton, 'copyButton')}
        title="Copy to Clipboard"
        onClick={handleCopyClick}
      />
    );

  const isEmailValid = email => EMAIL_REGEX.test(email);
  const externalLinkUrl = isEmailValid(linkProps?.url)
    ? { href: `mailto:${linkProps?.url}` }
    : null;

  const infoLabelTypes = {
    text: (
      <Typography
        role="definition"
        component="dd"
        variant={variant}
        title={description}
        className={clsx(classes.infoLabelText, classes.infoLabel)}
        noWrap={cropped}
      >
        {description}
        {renderCopyButton({ color: THEME.palette.grey[600] })}
      </Typography>
    ),
    localLink: description ? (
      <div className={classes.linkContainer}>
        {avatarProps && (
          <div className={classes.avatarLink}>
            <FPAvatar {...avatarProps} />
          </div>
        )}

        <FPLink
          linkProps={{ type: 'local', ...linkProps }}
          className={clsx(classes.infoLabel, className)}
          itemId={itemId}
        >
          {description}
          {renderCopyButton({ color: colors.navLink })}
        </FPLink>
      </div>
    ) : null,
    externalLink: (
      <div className={classes.linkContainer}>
        {linkProps?.icon && <div className={classes.emailValidationIcon}>{linkProps.icon}</div>}
        <FPLink
          linkProps={{ type: 'external', target: '_blank', ...linkProps }}
          {...externalLinkUrl}
        />

        {linkProps?.url ? renderCopyButton({ color: colors.navLink }) : null}
      </div>
    ),
    ringCentral: (
      <>
        <div className={classes.linkContainer}>
          <FPLink linkProps={{ type: 'ringCentral', ...linkProps }} />
          <span>{description}</span>
        </div>
        {secondaryText && (
          <Typography role="term" title={secondaryText} variant="subtitle2">
            {secondaryText}
          </Typography>
        )}
      </>
    ),
    chip: (
      <InfoLabelChips
        values={Array.isArray(description) ? description : [description]}
        labelKey={labelKey}
        baseClass={baseClass}
      />
    )
  };

  /* IMPORTANT: this atom is being rendering excecively into the DOM (specially in the tabs page)
     Use the least amount possible of components/tags in order to prevent slow deficient rendering. */
  return (
    <>
      {isLoading ? (
        <Skeleton style={styles.titleSkeleton} role="progressbar" />
      ) : (
        <Typography
          component="dt"
          noWrap
          role="term"
          title={title}
          variant="overline"
          {...textProps}
        >
          {title} {Icon && <Icon size={16} />}
        </Typography>
      )}

      {isLoading ? (
        <Skeleton style={styles.descriptionSkeleton} role="progressbar" />
      ) : (
        customValueRender || infoLabelTypes[mode]
      )}
    </>
  );
};

InfoLabel.defaultProps = {
  cropped: true,
  colorIndicator: undefined,
  description: '',
  isLoading: false,
  mode: 'text',
  linkProps: undefined,
  canCopy: false,
  className: null,
  customValueRender: null,
  avatarProps: undefined,
  itemId: '',
  variant: 'subtitle1',
  textProps: null,
  secondaryText: null,
  Icon: null
};

export default InfoLabel;
